
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface InternalResetFormProps {
  onBack: () => void;
}

const InternalResetForm = ({ onBack }: InternalResetFormProps) => {
  const { resetPassword, resetPin, loading } = useInternalAuth();
  const [activeTab, setActiveTab] = useState<'password' | 'pin'>('password');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    seedPhrase: '',
    newPassword: '',
    newPin: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.username || !formData.seedPhrase || !formData.newPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    const seedPhraseArray = formData.seedPhrase.trim().split(/\s+/);
    if (seedPhraseArray.length !== 12) {
      setError('Seed phrase must contain exactly 12 words');
      return;
    }

    const result = await resetPassword(formData.username, seedPhraseArray, formData.newPassword);
    if (result.success) {
      setSuccess('Password reset successfully! You can now login with your new password.');
      setFormData({ ...formData, newPassword: '', seedPhrase: '' });
    } else {
      setError(result.error || 'Password reset failed');
    }
  };

  const handlePinReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.username || !formData.seedPhrase || !formData.newPin) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.newPin.length < 4) {
      setError('PIN must be at least 4 characters long');
      return;
    }

    const seedPhraseArray = formData.seedPhrase.trim().split(/\s+/);
    if (seedPhraseArray.length !== 12) {
      setError('Seed phrase must contain exactly 12 words');
      return;
    }

    const result = await resetPin(formData.username, seedPhraseArray, formData.newPin);
    if (result.success) {
      setSuccess('PIN reset successfully! You can now use your new PIN.');
      setFormData({ ...formData, newPin: '', seedPhrase: '' });
    } else {
      setError(result.error || 'PIN reset failed');
    }
  };

  return (
    <div className="space-y-4">
      <Button
        type="button"
        variant="ghost"
        onClick={onBack}
        className="text-gray-400 hover:text-white p-0 h-auto"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Login
      </Button>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded text-sm">
          {success}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'password' | 'pin')} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4 bg-gray-900">
          <TabsTrigger value="password" className="text-white data-[state=active]:bg-sports-green data-[state=active]:text-black">Reset Password</TabsTrigger>
          <TabsTrigger value="pin" className="text-white data-[state=active]:bg-sports-green data-[state=active]:text-black">Reset PIN</TabsTrigger>
        </TabsList>

        <TabsContent value="password">
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username-pwd" className="text-white">Username</Label>
              <Input
                id="username-pwd"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Enter your username"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seedphrase-pwd" className="text-white">Recovery Seed Phrase</Label>
              <Input
                id="seedphrase-pwd"
                type="text"
                value={formData.seedPhrase}
                onChange={(e) => setFormData({ ...formData, seedPhrase: e.target.value })}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Enter your 12-word seed phrase separated by spaces"
                required
              />
              <p className="text-xs text-gray-400">Enter all 12 words separated by spaces</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newpassword" className="text-white">New Password</Label>
              <div className="relative">
                <Input
                  id="newpassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white pr-12"
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sports-green text-black hover:opacity-90"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="pin">
          <form onSubmit={handlePinReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username-pin" className="text-white">Username</Label>
              <Input
                id="username-pin"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Enter your username"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seedphrase-pin" className="text-white">Recovery Seed Phrase</Label>
              <Input
                id="seedphrase-pin"
                type="text"
                value={formData.seedPhrase}
                onChange={(e) => setFormData({ ...formData, seedPhrase: e.target.value })}
                className="bg-gray-900 border-gray-700 text-white"
                placeholder="Enter your 12-word seed phrase separated by spaces"
                required
              />
              <p className="text-xs text-gray-400">Enter all 12 words separated by spaces</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newpin" className="text-white">New PIN</Label>
              <div className="relative">
                <Input
                  id="newpin"
                  type={showNewPin ? 'text' : 'password'}
                  value={formData.newPin}
                  onChange={(e) => setFormData({ ...formData, newPin: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white pr-12"
                  placeholder="Enter new PIN"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPin(!showNewPin)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-white"
                >
                  {showNewPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-sports-green text-black hover:opacity-90"
            >
              {loading ? 'Resetting PIN...' : 'Reset PIN'}
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InternalResetForm;
